import React from 'react';
import { connect } from 'react-redux';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import Dialog from '@material-ui/core/Dialog';

import SectionEyebrow from '../LandingSections/SectionEyebrow';
import SubscriptionsDigitaliada from '../../pages/Subscriptions/SubscriptionsDigitaliada';
import store from '../../redux/store';
import { upgradeSubscriptionDialog } from '../../redux/actions';
import { featureList, getIntlCount, replaceValues } from '../../common/features';
import { SUBSCRIPTION_PLANS, TRIAL_DAYS } from '../../shared/Capabilities';
import { ONBOARDING_STEPS } from '../../shared/OnboardingOptions';
import { SUBSCRIPTIONS_LINK } from '../../common/urlLinks';
import { CARD, PILL_PRIMARY, PILL_SECONDARY, TEXT_LINK, cx } from '../../pages/Home/HomeDashboard/dashboardStyles';
import { REDESIGN_COLORS, REDESIGN_FONTS } from '../../constants';

// Which plan feature each upsell reason is about, so the plan cards can point
// at the row that unlocks it.
const UPSELL_FEATURE = {
    'subscription.upsell.groups.exceeded': 'availableGroups',
    'subscription.upsell.groups.unavailable': 'availableGroups',
    'subscription.upsell.library.exceeded': 'availableLibrary',
    'subscription.upsell.library.unavailable': 'availableLibrary',
    'subscription.upsell.multiBoards.unavailable': 'multipleBoarsInIndividualSessions',
    'subscription.upsell.pdf.unavailable': 'pdfDownload',
    'subscription.upsell.mathConverter.unavailable': 'mathConverter',
    'subscription.upsell.audioVideo.unavailable': 'audioVideo',
    'subscription.upsell.multiBoard.exceeded': 'savedMultiBoardSessionCount',
};

const useStyles = makeStyles(() => ({
    paper: {
        boxSizing: 'border-box',
        width: 'min(960px, calc(100vw - 32px))',
        maxWidth: 'none',
        margin: '16px',
        padding: 'clamp(22px, 3vw, 36px)',
        borderRadius: '28px',
        background: REDESIGN_COLORS.cream,
        fontFamily: REDESIGN_FONTS.body,
        color: REDESIGN_COLORS.dark,
    },
    head: {
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: '16px',
        marginBottom: '22px',
    },
    eyebrowRow: {
        marginBottom: '14px',
    },
    title: {
        margin: '0 0 10px',
        fontSize: 'clamp(1.35rem, 2.6vw, 1.9rem)',
        lineHeight: 1.15,
        letterSpacing: '-0.03em',
        fontWeight: REDESIGN_FONTS.weightHeading,
        color: REDESIGN_COLORS.dark,
        textWrap: 'balance',
    },
    lead: {
        margin: 0,
        maxWidth: '620px',
        color: REDESIGN_COLORS.body,
        fontSize: '0.98rem',
        lineHeight: 1.6,
    },
    close: {
        flexShrink: 0,
        display: 'grid',
        placeItems: 'center',
        width: '40px',
        height: '40px',
        padding: 0,
        border: `1px solid ${REDESIGN_COLORS.line}`,
        borderRadius: '50%',
        color: REDESIGN_COLORS.dark,
        background: REDESIGN_COLORS.white,
        cursor: 'pointer',
        '&:hover': {
            borderColor: REDESIGN_COLORS.primary,
            color: REDESIGN_COLORS.link,
        },
    },
    grid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 260px), 1fr))',
        gap: '16px',
    },
    card: {
        ...CARD,
        display: 'flex',
        flexDirection: 'column',
        padding: '22px',
    },
    cardFeatured: {
        borderColor: REDESIGN_COLORS.primary,
        boxShadow: '0 0 0 3px rgba(16, 169, 179, 0.15)',
    },
    planName: {
        margin: '12px 0 8px',
        fontSize: '1.3rem',
        letterSpacing: '-0.025em',
        fontWeight: REDESIGN_FONTS.weightHeading,
        color: REDESIGN_COLORS.dark,
    },
    price: {
        display: 'flex',
        alignItems: 'baseline',
        flexWrap: 'wrap',
        gap: '6px',
        margin: '0 0 2px',
    },
    amount: {
        fontSize: '2rem',
        lineHeight: 1,
        letterSpacing: '-0.04em',
        fontWeight: REDESIGN_FONTS.weightHeading,
        color: REDESIGN_COLORS.dark,
    },
    unit: {
        color: REDESIGN_COLORS.body,
        fontSize: '0.92rem',
        fontWeight: REDESIGN_FONTS.weightStrong,
    },
    note: {
        margin: '0 0 16px',
        color: REDESIGN_COLORS.muted,
        fontSize: '0.84rem',
        fontWeight: 600,
    },
    bullets: {
        display: 'grid',
        gap: '8px',
        margin: '0 0 18px',
        padding: 0,
        listStyle: 'none',
        color: REDESIGN_COLORS.dark,
        fontSize: '0.9rem',
        fontWeight: REDESIGN_FONTS.weightStrong,
    },
    bullet: {
        display: 'flex',
        gap: '9px',
        padding: '2px 0',
    },
    bulletOff: {
        color: REDESIGN_COLORS.muted,
        fontWeight: 500,
    },
    // The row that answers the upsell.
    bulletHot: {
        margin: '0 -10px',
        padding: '6px 10px',
        borderRadius: '10px',
        background: REDESIGN_COLORS.amberBadge,
    },
    tick: {
        color: REDESIGN_COLORS.primary,
        fontWeight: REDESIGN_FONTS.weightHeading,
    },
    tickOff: {
        color: REDESIGN_COLORS.line,
    },
    // The trial label is long; let the pills wrap instead of clipping.
    cta: {
        ...PILL_PRIMARY,
        marginTop: 'auto',
        width: '100%',
        whiteSpace: 'normal',
        textAlign: 'center',
        lineHeight: 1.2,
        padding: '12px 22px',
    },
    ctaSecondary: {
        ...PILL_SECONDARY,
        marginTop: 'auto',
        width: '100%',
        whiteSpace: 'normal',
        textAlign: 'center',
        lineHeight: 1.2,
        padding: '12px 22px',
    },
    guest: {
        margin: 'auto 0 0',
        color: REDESIGN_COLORS.body,
        fontSize: '0.86rem',
        lineHeight: 1.5,
    },
    foot: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: '12px',
        marginTop: '22px',
    },
    link: TEXT_LINK,
    dismiss: {
        ...PILL_SECONDARY,
        minHeight: '44px',
    },
}));

/**
 * The upsell dialog: opened when a feature or quota is not in the account's
 * plan. Names the reason, then shows each plan that would unlock it as a
 * card, with the unlocking row highlighted and a link to start that plan.
 */
const UpgradeSubscriptionDialog = ({ appState, subscriptions, pricing, profile }) => {
    const classes = useStyles();
    const intl = useIntl();
    const upsell = appState.upgradeSubscription;
    const open = !!upsell;

    const close = () => store.dispatch(upgradeSubscriptionDialog(null));

    const isEur = intl.locale !== 'ro';
    const t = (id, values) => intl.formatMessage({ id: `redesign.home.pricing.${id}` }, values);

    const plans = open && subscriptions ? (upsell.subscriptions || []).filter((type) => subscriptions[type]) : [];
    const hot = open ? UPSELL_FEATURE[upsell.key] : null;

    const featuresOf = (type) => {
        const list = featureList.map((f) => ({ ...f }));
        replaceValues(subscriptions[type], list);
        return list;
    };

    const priceOf = (type) => {
        const list = pricing && pricing[type];
        if (!Array.isArray(list)) return null;
        const price = list.find((p) => p.currency === (isEur ? 'eur' : 'ron') && p.interval === 'year');
        if (!price) return null;
        const amount = price.unit_amount / 100;
        return {
            amount: intl.formatNumber(amount, { maximumFractionDigits: 2 }),
            unit: `${isEur ? '€' : 'lei'} ${t('period.year')}`,
            note: t('perMonthEquivalent', {
                amount: intl.formatNumber(amount / 12, {
                    style: 'currency',
                    currency: isEur ? 'EUR' : 'RON',
                    maximumFractionDigits: 2,
                }),
            }),
        };
    };

    const onboarding = (type) =>
        `/onboarding?step=${ONBOARDING_STEPS.START}&subscription=${type}&billAnnual=true&billEur=${isEur}`;

    return (
        <Dialog open={open} onClose={close} classes={{ paper: classes.paper }} aria-labelledby="upsell-title">
            <div className={classes.head}>
                <div>
                    <div className={classes.eyebrowRow}>
                        <SectionEyebrow>
                            <FormattedMessage id="redesign.upsell.eyebrow" />
                        </SectionEyebrow>
                    </div>
                    <h2 id="upsell-title" className={classes.title}>
                        {upsell && upsell.key ? (
                            <FormattedMessage id={upsell.key} />
                        ) : (
                            <FormattedMessage id="subscription.upsell.dialog.title" />
                        )}
                    </h2>
                    <p className={classes.lead}>
                        <FormattedMessage id="redesign.upsell.lead" />
                    </p>
                </div>
                <button
                    type="button"
                    onClick={close}
                    aria-label={intl.formatMessage({ id: 'close' })}
                    className={classes.close}
                >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true">
                        <path d="M6 6l12 12M18 6L6 18" />
                    </svg>
                </button>
            </div>

            {window.disableTracking ? (
                <SubscriptionsDigitaliada />
            ) : (
                <div className={classes.grid}>
                    {plans.map((type, index) => {
                        const trial = type === SUBSCRIPTION_PLANS.TRIAL;
                        const price = trial ? null : priceOf(type);

                        return (
                            <article key={type} className={cx(classes.card, index === 0 && classes.cardFeatured)}>
                                <SectionEyebrow variant="card">
                                    {trial ? (
                                        <FormattedMessage id="redesign.upsell.trial" />
                                    ) : (
                                        <FormattedMessage id="redesign.upsell.unlocks" />
                                    )}
                                </SectionEyebrow>
                                <h3 className={classes.planName}>
                                    <FormattedMessage id={`subscriptions.subscription.${type}.name`} />
                                </h3>
                                {price ? (
                                    <>
                                        <div className={classes.price}>
                                            <span className={classes.amount}>{price.amount}</span>
                                            <span className={classes.unit}>{price.unit}</span>
                                        </div>
                                        <p className={classes.note}>{price.note}</p>
                                    </>
                                ) : trial ? (
                                    <p className={classes.note}>
                                        {intl.formatMessage({ id: 'redesign.pricing.hero.trust.noCard' })}
                                    </p>
                                ) : null}

                                <ul className={classes.bullets}>
                                    {featuresOf(trial ? SUBSCRIPTION_PLANS.PREMIUM : type).map((f) => {
                                        const isCount = typeof f.defaultValue !== 'boolean';
                                        const included = isCount ? f.defaultValue !== 0 : f.defaultValue;
                                        const formatObj = { [f.name]: getIntlCount(intl, f.defaultValue) };
                                        const label = intl.formatMessage(
                                            {
                                                id:
                                                    (isCount ? 'redesign.subscriptions.feature.' : 'subscriptions.feature.') +
                                                    f.name +
                                                    '.name',
                                            },
                                            formatObj
                                        );
                                        const value = isCount
                                            ? intl.formatMessage({ id: 'subscriptions.feature.' + f.name + '.name' }, formatObj)
                                            : null;
                                        return (
                                            <li
                                                key={f.name}
                                                className={cx(
                                                    classes.bullet,
                                                    !included && classes.bulletOff,
                                                    f.name === hot && included && classes.bulletHot
                                                )}
                                            >
                                                <span aria-hidden="true" className={included ? classes.tick : classes.tickOff}>
                                                    {included ? '✓' : '—'}
                                                </span>
                                                {value ? label + ': ' + value : label}
                                            </li>
                                        );
                                    })}
                                </ul>

                                {profile && profile.isGuest ? (
                                    <p className={classes.guest}>
                                        <FormattedMessage id="profile.guest.upgradeNotice" />
                                    </p>
                                ) : (
                                    <Link
                                        to={onboarding(type)}
                                        onClick={close}
                                        className={index === 0 ? classes.cta : classes.ctaSecondary}
                                    >
                                        {trial ? (
                                            <FormattedMessage
                                                id="subscriptions.subscription.startTrial"
                                                values={{ days: TRIAL_DAYS }}
                                            />
                                        ) : (
                                            <FormattedMessage id="redesign.home.pricing.basic.cta" />
                                        )}
                                        <span aria-hidden="true">→</span>
                                    </Link>
                                )}
                            </article>
                        );
                    })}
                </div>
            )}

            <div className={classes.foot}>
                <Link to={SUBSCRIPTIONS_LINK} onClick={close} className={classes.link}>
                    <FormattedMessage id="redesign.cta.plans" />
                    <span aria-hidden="true">→</span>
                </Link>
                <button type="button" onClick={close} className={classes.dismiss}>
                    <FormattedMessage id="close" />
                </button>
            </div>
        </Dialog>
    );
};

const mapStateToProps = (state) => {
    const { appState, mainAppState, profile } = state;
    return { appState, subscriptions: mainAppState.subscriptions, pricing: mainAppState.pricing, profile };
};

export default connect(mapStateToProps)(UpgradeSubscriptionDialog);
